import { Box, Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { DoorProps } from '../../@types/Components';
import { EInteractionMode, EOpeningDirection, EOrientation, ESubMenu, EUnitType } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { INTERNAL_TO_CM_FACTOR, UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { snapDoorToWall } from '../../utils/wallElementSnapping';
import DoorLengthIndicator from './indicators/DoorLengthIndicator';

export { snapDoorToWall };

const Door = ({ onClick, selectedDoor, wallModel, onWallUpdate, door, wallIndex, doorIndex }: DoorProps): React.JSX.Element => {
  const { interactionMode, setCurrentlyActiveSubMenu } = useToolbarContext();
  const { showDoorLength } = useMultiSelectToggleContext();
  const { setDoorWidthAndOpeningDirAttributes, getGermanDoorDirectionText } = useBuilding();
  const { unitType } = useUnitContext();
  const [hovered, setHovered] = useState<boolean>(false);
  const { length: width, angle, orientation, openingDirection } = door;
  const groupRef = useRef<THREE.Group<THREE.Object3DEventMap>>(null);
  const [doorWidth, setDoorWidth] = useState<number>(width);

  const widthCm = door.length * INTERNAL_TO_CM_FACTOR;
  const widthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(widthCm.toFixed(3))
      : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const widthUnit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  useEffect(() => {
    const dinGermanText = getGermanDoorDirectionText(openingDirection, orientation);
    setDoorWidthAndOpeningDirAttributes(wallIndex, doorIndex, widthValue, widthUnit, dinGermanText);
  }, [setDoorWidthAndOpeningDirAttributes, wallIndex, doorIndex, widthValue, widthUnit, openingDirection, orientation, width]);

  useEffect(() => {
    setDoorWidth(width);
  }, [width]);

  const meshColor = useMemo(
    () => (selectedDoor.wallIndex !== undefined && selectedDoor.doorIndex !== undefined ? '#009fe3' : hovered ? '#888888' : 'white'),
    [hovered, selectedDoor],
  );

  const constraintFunction = useCallback((newPosition: THREE.Vector3): THREE.Vector3 => snapDoorToWall(wallModel, newPosition, width), [wallModel, width]);

  const updateDoorPosition = useCallback(
    (newPosition: THREE.Vector3): void => {
      door.position = newPosition;
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.DOOR_POSITION_CHANGED);
    },
    [onWallUpdate, wallIndex, wallModel, door],
  );

  useDraggableMesh(groupRef, constraintFunction, updateDoorPosition, interactionMode !== EInteractionMode.MOVE, interactionMode !== EInteractionMode.MOVE);

  // --- NEU: Koordinaten für die fette Rand-Linie ---
  const doorBoxOutlinePoints = useMemo(() => {
    const hw = doorWidth / 2;
    const hd = wallModel.depth / 2;
    return [
      new THREE.Vector3(-hw, hd, 0),
      new THREE.Vector3(hw, hd, 0),
      new THREE.Vector3(hw, -hd, 0),
      new THREE.Vector3(-hw, -hd, 0),
      new THREE.Vector3(-hw, hd, 0), // Schließt das Rechteck
    ];
  }, [doorWidth, wallModel.depth]);

  const [doorOpeningPoints] = useMemo(() => {
    const newA = new THREE.Vector2(width / 2, 0);
    const newB = new THREE.Vector2(-width / 2, 0);

    newA.y -= wallModel.depth / 2;
    newB.y -= wallModel.depth / 2;

    const newAB = new THREE.Vector2().subVectors(newB, newA);
    const perpendicular = new THREE.Vector2(-newAB.y, newAB.x);
    const unit = perpendicular.normalize().multiplyScalar(width);

    const doorOpenPointLeft = new THREE.Vector2().addVectors(newA, unit);
    const doorOpenPointRight = new THREE.Vector2().addVectors(newB, unit);

    const doorOpenPath = new THREE.Path();
    let radius = 0;
    let startAngle = 0;
    let endAngle = 0;
    let arcCurve = null;
    let arcPoints = [];
    let center = null;
    const curveSegments = 20;

    if (openingDirection === EOpeningDirection.LEFT) {
      doorOpenPath.moveTo(newA.x, newA.y);
      doorOpenPath.lineTo(doorOpenPointLeft.x, doorOpenPointLeft.y);
      center = newA;
      radius = Math.abs(newB.x - newA.x);
      startAngle = Math.atan2(newB.y - center.y, newB.x - center.x);
      endAngle = Math.atan2(doorOpenPointLeft.y - center.y, doorOpenPointLeft.x - center.x);
      arcCurve = new THREE.ArcCurve(newA.x, newA.y, radius, startAngle, endAngle, false);
      arcPoints = arcCurve.getPoints(curveSegments);
      doorOpenPath.moveTo(newA.x, newA.y);
    } else {
      doorOpenPath.moveTo(newB.x, newB.y);
      doorOpenPath.lineTo(doorOpenPointRight.x, doorOpenPointRight.y);
      center = newB;
      radius = Math.abs(newB.x - newA.x);
      startAngle = Math.atan2(newA.y - center.y, newA.x - center.x);
      endAngle = Math.atan2(doorOpenPointRight.y - center.y, doorOpenPointRight.x - center.x);
      arcCurve = new THREE.ArcCurve(newB.x, newB.y, radius, startAngle, endAngle, true);
      arcPoints = arcCurve.getPoints(curveSegments);
      doorOpenPath.moveTo(newB.x, newB.y);
    }
    arcPoints.forEach((point) => {
      doorOpenPath.lineTo(point.x, point.y);
    });

    const pathPoints = doorOpenPath.getPoints();

    if (orientation === EOrientation.OUTWARD) {
      const newA = new THREE.Vector2(-width / 2, 0);
      const newB = new THREE.Vector2(width / 2, 0);
      const middle = new THREE.Vector2().lerpVectors(newA, newB, 0.5);
      for (const point of pathPoints) {
        point.rotateAround(middle, Math.PI);
      }
    }

    return [pathPoints];
  }, [width, wallModel.depth, orientation, openingDirection]);

  const handlePointerEvents = (event: ThreeEvent<PointerEvent>, callback: (e: ThreeEvent<PointerEvent>) => void) => {
    event.stopPropagation();
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    if (interactionMode !== EInteractionMode.MOVE) {
      callback && callback(event);
    }
  };

  return (
    <group name={`group-door#${selectedDoor.doorIndex}-wall#${selectedDoor.wallIndex}`} ref={groupRef} position={door.position} rotation={[0, 0, angle]}>
      {(hovered || (selectedDoor && selectedDoor.wallIndex !== undefined && selectedDoor.doorIndex !== undefined) || showDoorLength) && (
        <DoorLengthIndicator doorWidth={doorWidth} wallSideALength={doorWidth / UNIT_TO_METER_DIVISOR} rotation={angle} />
      )}
      <group position={[0, 0, 0.02]}>
        <Box
          name={'door-box'}
          args={[doorWidth, wallModel.depth, 1]}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            handlePointerEvents(e, onClick);
          }}
          onPointerEnter={(e) => handlePointerEvents(e, () => setHovered(true))}
          onPointerLeave={(e) => handlePointerEvents(e, () => setHovered(false))}
          renderOrder={RENDER_ORDERS.door}
        >
          <meshBasicMaterial name={'door-box-meshBasicMaterial'} attach="material" color={meshColor} transparent={true} />
        </Box>

        {/* DIE SAUBERE LÖSUNG FÜR DIE FETTE LINIE */}
        <Line points={doorBoxOutlinePoints} color="black" lineWidth={1.8} depthTest={false} renderOrder={RENDER_ORDERS.door + 1} transparent={true} />

        <Line name={'door-openArc'} points={doorOpeningPoints} color="black" lineWidth={1.8} depthTest={false} renderOrder={RENDER_ORDERS.door} transparent={true} />
      </group>
    </group>
  );
};

export default memo(Door);
